import { Types as CoreTypes, getEnabledElement } from '@cornerstonejs/core';
import {
  AnnotationDisplayTool,
  Types as ToolsTypes,
  annotation as annotationTools,
  drawing,
  utilities,
} from '@cornerstonejs/tools';
import { vec2 } from 'gl-matrix';

type IEnabledElement = CoreTypes.IEnabledElement;
type SVGDrawingHelper = ToolsTypes.SVGDrawingHelper;
type StyleSpecifier = ToolsTypes.AnnotationStyle.StyleSpecifier;
type Annotation = ToolsTypes.Annotation;

function computeTextBoxCoordinates(
  coordinates: CoreTypes.Point2[]
): CoreTypes.Point2 {
  return [
    Math.min(...coordinates.map((coord) => coord[0])),
    Math.min(...coordinates.map((coord) => coord[1])) - 22,
  ];
}

class GleamerPredictionRectangleTool extends AnnotationDisplayTool {
  /**
   * A display-only tool has no in-progress manipulation to cancel, but it still
   * has to answer the call. `cancelActiveManipulations` invokes `cancel()`
   * unconditionally on every Active/Passive tool holding an annotation on the
   * element, and `AnnotationDisplayTool` — unlike the interactive tool bases —
   * never defines one.
   *
   * That went unnoticed until 3.13: upstream 661ecb5b2e (#6104) made
   * MEASUREMENT_REMOVED run `cancelMeasurement` on every removal, where 3.12.9
   * did nothing. Deleting any observation while a prediction was drawn on the
   * current slice therefore threw `tool.cancel is not a function`, which
   * propagated out of MeasurementService._broadcastEvent and took down the
   * right panel through its ErrorBoundary.
   *
   * Returns undefined deliberately. A truthy return means "I cancelled this
   * annotation" and makes the helper stop before polling the tools after us,
   * which would mask a genuine in-progress drawing on another tool.
   */
  cancel(): undefined {
    return undefined;
  }

  renderAnnotation(
    enabledElement: IEnabledElement,
    svgDrawingHelper: SVGDrawingHelper
  ) {
    let renderStatus = false;
    const { viewport } = enabledElement;
    const { element } = viewport;

    const groupKey = annotationTools.state
      .getAnnotationManager()
      .getGroupKey(element);

    let annotations = annotationTools.state
      .getAnnotationManager()
      .getAnnotations(groupKey, this.getToolName());

    if (!annotations?.length) {
      return renderStatus;
    }

    annotations = this.filterInteractableAnnotationsForElement(
      element,
      annotations
    );

    if (!annotations?.length) {
      return renderStatus;
    }

    const styleSpecifier: StyleSpecifier = {
      toolGroupId: this.toolGroupId,
      toolName: this.getToolName(),
      viewportId: enabledElement.viewport.id,
    };

    for (let i = 0; i < annotations.length; i++) {
      const annotation = annotations[i] as Annotation;
      const { annotationUID, data } = annotation;
      const { points } = data.handles;

      const canvasCoordinates = points.map((p) => viewport.worldToCanvas(p));

      styleSpecifier.annotationUID = annotationUID;
      const lineWidth = this.getStyle('lineWidth', styleSpecifier, annotation);
      const lineDash = this.getStyle('lineDash', styleSpecifier, annotation);
      const color = this.getStyle('color', styleSpecifier, annotation);

      if (!viewport.getRenderingEngine()) {
        console.warn('Rendering Engine has been destroyed');
        return renderStatus;
      }

      if (!annotationTools.visibility.isAnnotationVisible(annotationUID)) {
        continue;
      }

      const rectangleUID = '0';
      drawing.drawRect(
        svgDrawingHelper,
        annotationUID,
        rectangleUID,
        canvasCoordinates[0],
        canvasCoordinates[1],
        {
          color,
          lineDash,
          lineWidth,
        }
      );

      const textBoxFontSize = this.getStyle(
        'textBoxFontSize',
        styleSpecifier,
        annotation
      );

      if (textBoxFontSize === '1') {
        drawing.drawTextBox(
          svgDrawingHelper,
          annotationUID,
          '0',
          [annotation.data.label],
          computeTextBoxCoordinates(canvasCoordinates),
          {
            padding: 0,
            background: 'rgba(0,0,0,0.5)',
            color: 'rgb(255,255,255)',
          }
        );
      }
    }

    return true;
  }

  getHandleNearImagePoint(
    element: HTMLDivElement,
    annotation: ToolsTypes.Annotation,
    canvasCoords: CoreTypes.Point2,
    proximity: number
  ): ToolsTypes.ToolHandle | undefined {
    const enabledElement = getEnabledElement(element);
    const { viewport } = enabledElement;

    const { data } = annotation;
    const { points } = data.handles;

    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const annotationCanvasCoordinate = viewport.worldToCanvas(point);

      const near =
        vec2.distance(canvasCoords, annotationCanvasCoordinate) < proximity;

      if (near === true) {
        data.handles.activeHandleIndex = i;
        return point;
      }
    }

    data.handles.activeHandleIndex = null;
  }

  _getRectangleImageCoordinates = (
    points: Array<CoreTypes.Point2>
  ): {
    left: number;
    top: number;
    width: number;
    height: number;
  } => {
    const [point0, point1] = points;

    return {
      left: Math.min(point0[0], point1[0]),
      top: Math.min(point0[1], point1[1]),
      width: Math.abs(point0[0] - point1[0]),
      height: Math.abs(point0[1] - point1[1]),
    };
  };

  /**
   * It returns if the canvas point is near the provided annotation in the provided
   * element or not. A proximity is passed to the function to determine the
   * proximity of the point to the annotation in number of pixels.
   *
   * @param element - HTML Element
   * @param annotation - Annotation
   * @param canvasCoords - Canvas coordinates
   * @param proximity - Proximity to tool to consider
   * @returns Boolean, whether the canvas point is near tool
   */
  isPointNearTool = (
    element: HTMLDivElement,
    annotation: Annotation,
    canvasCoords: CoreTypes.Point2,
    proximity: number,
    interactionType: string
  ): boolean => {
    const enabledElement = getEnabledElement(element);
    const { viewport } = enabledElement;

    const { data } = annotation;
    const { points } = data.handles;

    const canvasPoint1 = viewport.worldToCanvas(points[0]);
    const canvasPoint2 = viewport.worldToCanvas(points[1]);

    const rect = this._getRectangleImageCoordinates([
      canvasPoint1,
      canvasPoint2,
    ]);

    const point = [canvasCoords[0], canvasCoords[1]];
    const { left, top, width, height } = rect;

    const distanceToPoint = utilities.math.rectangle.distanceToPoint(
      [left, top, width, height],
      point as CoreTypes.Point2
    );

    return distanceToPoint <= proximity;
  };

  toolSelectedCallback = (
    evt: ToolsTypes.EventTypes.InteractionEventType,
    annotation: Annotation
  ): void => {
    console.log('toolSelectedCallback', evt, annotation);
  };

  handleSelectedCallback = (
    evt: ToolsTypes.EventTypes.InteractionEventType,
    annotation: Annotation,
    handle: ToolsTypes.ToolHandle
  ): void => {
    console.log('handleSelectedCallback', evt, annotation, handle);
  };

  /**
   * Returns true if the `canvasCoords` are near a handle or selectable part of the tool
   *
   * @param element - The HTML element
   * @param annotation - The annotation to check
   * @param canvasCoords - The canvas coordinates to check
   * @param proximity - The proximity to consider
   *
   * @returns If the point is near.
   */
  private _imagePointNearToolOrHandle(
    element: HTMLDivElement,
    annotation: Annotation,
    canvasCoords: CoreTypes.Point2,
    proximity: number
  ): boolean {
    // Based on the tool instance type, check if the point is near the tool handles
    const handleNearImagePoint = this.getHandleNearImagePoint(
      element,
      annotation,
      canvasCoords,
      proximity
    );

    if (handleNearImagePoint) {
      return true;
    }

    // If the point is not near the handles, check if the point is near the tool
    const toolNewImagePoint = this.isPointNearTool(
      element,
      annotation,
      canvasCoords,
      proximity,
      'mouse'
    );

    if (toolNewImagePoint) {
      return true;
    }
  }

  /**
   * @virtual Event handler for Cornerstone MOUSE_MOVE event.
   *
   *
   * @param evt - The normalized mouse event
   * @param filteredAnnotations - The annotations to check for hover interactions
   * @returns True if the annotation needs to be re-drawn by the annotationRenderingEngine.
   */
  public mouseMoveCallback = (
    evt: ToolsTypes.EventTypes.MouseMoveEventType,
    filteredAnnotations?: ToolsTypes.Annotations
  ): boolean => {
    if (!filteredAnnotations) {
      return false;
    }

    const { element, currentPoints } = evt.detail;
    const canvasCoords = currentPoints.canvas;
    let annotationsNeedToBeRedrawn = false;

    for (const annotation of filteredAnnotations) {
      const { data } = annotation;
      const activateHandleIndex = data.handles
        ? data.handles.activeHandleIndex
        : undefined;

      // Perform tool specific imagePointNearToolOrHandle to determine if the mouse
      // is near the tool or its handles or its textBox.
      const near = this._imagePointNearToolOrHandle(
        element,
        annotation,
        canvasCoords,
        6 // Todo: This should come from the state
      );

      const nearToolAndNotMarkedActive = near && !annotation.highlighted;
      const notNearToolAndMarkedActive = !near && annotation.highlighted;
      if (nearToolAndNotMarkedActive || notNearToolAndMarkedActive) {
        annotation.highlighted = !annotation.highlighted;
        annotationsNeedToBeRedrawn = true;
      } else if (
        data.handles &&
        data.handles.activeHandleIndex !== activateHandleIndex
      ) {
        // Active handle index has changed, re-render.
        annotationsNeedToBeRedrawn = true;
      }
    }

    return annotationsNeedToBeRedrawn;
  };
}

GleamerPredictionRectangleTool.toolName = 'GleamerPredictionRectangleTool';
export default GleamerPredictionRectangleTool;
